import type { ExamQuestion } from '../components/types';
import { examQuestionUiType } from './examQuestionUi';

/**
 * One question entry inside POST `questionAnswer` for `vidyarthi/exam/answer` and `vidyarthi/exam/submit`:
 * `"473": { "answer": "true" }` or `"471": { "matchPairAnswer": { "left": "right" } }`
 */
export type ExamQuestionAnswerPayloadEntry =
  | { answer: string }
  | { matchPairAnswer: Record<string, string> };

export type ExamQuestionAnswerPayloadMap = Record<string, ExamQuestionAnswerPayloadEntry>;

function optionTextForValue(question: ExamQuestion, raw: string): string {
  const t = raw.trim();
  if (t === '') return '';
  const opts = question.options ?? [];
  const asNum = Number(t);
  if (Number.isFinite(asNum)) {
    const byId = opts.find((o) => o.optionId === asNum);
    if (byId) return String(byId.optionText ?? '').trim();
  }
  const byText = opts.find((o) => String(o.optionText).trim() === t);
  if (byText) return String(byText.optionText ?? '').trim();
  return t;
}

export function buildQuestionAnswerEntry(
  question: ExamQuestion | undefined,
  value: unknown
): ExamQuestionAnswerPayloadEntry | null {
  if (!question) return null;

  if (examQuestionUiType(question) === 'match_pair') {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
    const pairs: Record<string, string> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim() !== '') pairs[k] = v;
    }
    if (Object.keys(pairs).length === 0) return null;
    return { matchPairAnswer: pairs };
  }

  let scalar: string | number | null = null;
  if (typeof value === 'string') scalar = value;
  else if (typeof value === 'number' && !Number.isNaN(value)) scalar = value;
  else if (Array.isArray(value) && value.length > 0) {
    scalar = String(value[0]);
  }
  if (scalar == null) return null;

  const text = optionTextForValue(question, String(scalar));
  if (text === '') return null;
  return { answer: text };
}

/** Build full `questionAnswer` from current `answers` state (only questions with a savable answer). */
export function buildFullQuestionAnswerMap(
  questions: ExamQuestion[],
  answers: Record<number, unknown>
): ExamQuestionAnswerPayloadMap {
  const out: ExamQuestionAnswerPayloadMap = {};
  for (const q of questions) {
    const val = answers[q.questionId];
    if (val === undefined) continue;
    const entry = buildQuestionAnswerEntry(q, val);
    if (entry != null) {
      out[String(q.questionId)] = entry;
    }
  }
  return out;
}
